import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import SectionTitle from '../components/SectionTitle';
import { getMenu } from '../services/menuService';

const DEFAULT_PLACEHOLDER = '/images/menu/placeholders/placeholder.jpg';

const LIST_CATEGORIES = [
  'Alkoholfreie Getränke',
  'Bier & Most',
  'Wein',
  'Kaffee & Tee',
  'Extras'
];

const Section = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 1.5rem;
`;

const HeaderImage = styled.div`
  height: 280px;
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 2rem;
  background:
    linear-gradient(rgba(40, 28, 24, 0.25), rgba(40, 28, 24, 0.25)),
    url('/images/menu-header.jpg') center/cover no-repeat;

  @media (max-width: 768px) {
    height: 210px;
    border-radius: 16px;
  }
`;

const Intro = styled.p`
  max-width: 760px;
  margin-bottom: 1.5rem;
  color: #5b524a;
  line-height: 1.6;
`;

const Toolbar = styled.div`
  display: grid;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const SearchWrapper = styled.div`
  position: relative;
  max-width: 520px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.9rem 1rem 0.9rem 2.7rem;
  border: 1px solid #eadfd0;
  border-radius: 999px;
  background: #fff;
  color: #2f2a26;
  font: inherit;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.04);
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    border-color: #7a1f2b;
    box-shadow: 0 0 0 4px rgba(122, 31, 43, 0.12);
  }

  &::placeholder {
    color: #8a7d71;
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #7a1f2b;
  font-size: 1rem;
`;

const ClearSearchButton = styled.button`
  position: absolute;
  right: 0.7rem;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: #efe6d9;
  color: #4a3f38;
  border-radius: 999px;
  padding: 0.35rem 0.65rem;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 700;

  &:hover {
    background: #e4d5c3;
  }
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const FilterButton = styled.button`
  border: none;
  border-radius: 999px;
  padding: 0.75rem 1rem;
  cursor: pointer;
  font-weight: 600;
  transition: 0.2s ease;
  background: ${({ $active }) => ($active ? '#7a1f2b' : '#efe6d9')};
  color: ${({ $active }) => ($active ? '#fff' : '#4a3f38')};

  &:hover {
    transform: translateY(-1px);
  }
`;

const CategorySection = styled.div`
  margin-bottom: 2.75rem;
`;

const CategoryTitle = styled.h3`
  color: #7a1f2b;
  margin-bottom: 1rem;
  font-size: 1.4rem;
`;

const Grid = styled.div`
  display: grid;
  gap: 1.25rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.article`
  background: #fff;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0e7dc;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 32px rgba(0, 0, 0, 0.1);
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 220px;
  background: #f7f1e8;
  overflow: hidden;
`;

const ItemImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.4s ease, filter 0.4s ease;

  ${Card}:hover & {
    transform: scale(1.04);
    filter: brightness(0.8);
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(32, 20, 17, 0.72),
    rgba(32, 20, 17, 0.18)
  );
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  padding: 1rem;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${Card}:hover & {
    opacity: 1;
  }
`;

const OverlayText = styled.div`
  color: #fff;
`;

const OverlayTitle = styled.div`
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 0.2rem;
`;

const OverlaySubtitle = styled.div`
  font-size: 0.85rem;
  opacity: 0.92;
`;

const PlaceholderLabel = styled.span`
  position: absolute;
  bottom: 12px;
  right: 12px;
  background: rgba(122, 31, 43, 0.9);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  z-index: 2;
`;

const CardContent = styled.div`
  padding: 1rem 1.1rem 1.1rem;
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
`;

const ItemName = styled.span`
  font-weight: 600;
  color: #2f2a26;
  line-height: 1.4;
`;

const ItemPrice = styled.span`
  font-weight: 700;
  color: #7a1f2b;
  white-space: nowrap;
`;

const ListPanel = styled.div`
  background: #fff;
  border: 1px solid #f0e7dc;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.045);
`;

const ListRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  align-items: baseline;
  padding: 1rem 1.15rem;
  border-bottom: 1px solid #f1e8dd;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fbf6ef;
  }

  @media (max-width: 520px) {
    padding: 0.9rem 1rem;
    gap: 0.75rem;
  }
`;

const ListItemName = styled.div`
  color: #2f2a26;
  font-weight: 650;
  line-height: 1.4;
`;

const ListItemDescription = styled.div`
  margin-top: 0.25rem;
  color: #756b62;
  font-size: 0.9rem;
  font-weight: 400;
  line-height: 1.4;
`;

const ListItemPrice = styled.div`
  color: #7a1f2b;
  font-weight: 800;
  white-space: nowrap;
`;

const EmptyState = styled.div`
  color: #6a6158;
  background: #fff;
  border: 1px solid #f0e7dc;
  border-radius: 16px;
  padding: 1.2rem;
  line-height: 1.6;
`;

const AllergenNote = styled.p`
  margin-top: 2rem;
  font-size: 0.9rem;
  color: #6a6158;
  line-height: 1.6;
  background: #f6efe6;
  border: 1px solid #eadfd0;
  border-radius: 14px;
  padding: 1rem 1.1rem;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(24, 17, 14, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 9999;
`;

const ModalContent = styled.div`
  position: relative;
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  overflow: auto;
  background: #fff;
  border-radius: 22px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.28);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 42px;
  height: 42px;
  border: none;
  border-radius: 50%;
  background: rgba(122, 31, 43, 0.95);
  color: #fff;
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  z-index: 3;
  transition: transform 0.2s ease, background 0.2s ease;

  &:hover {
    transform: scale(1.05);
    background: #641822;
  }
`;

const ModalImage = styled.img`
  width: 100%;
  height: 340px;
  object-fit: cover;
  display: block;

  @media (max-width: 640px) {
    height: 240px;
  }
`;

const ModalBody = styled.div`
  padding: 1.3rem 1.3rem 1.5rem;
`;

const ModalTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 0.75rem;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #2f2a26;
  font-size: 1.6rem;
`;

const ModalPrice = styled.div`
  color: #7a1f2b;
  font-weight: 800;
  font-size: 1.25rem;
  white-space: nowrap;
`;

const ModalDescription = styled.p`
  margin: 0 0 1rem 0;
  color: #5f564f;
  line-height: 1.7;
`;

const ModalInfoBox = styled.div`
  background: #f8f3ec;
  border: 1px solid #eadfd0;
  border-radius: 14px;
  padding: 1rem;
  color: #5f564f;
  line-height: 1.6;
`;

const ModalInfoTitle = styled.div`
  font-weight: 700;
  color: #7a1f2b;
  margin-bottom: 0.35rem;
`;

function formatPrice(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const rawValue = String(value).trim();

  if (!rawValue) {
    return '';
  }

  const valueWithoutEuro = rawValue.replace('€', '').trim();
  const normalizedValue = valueWithoutEuro.replace(',', '.');
  const numericValue = Number(normalizedValue);

  if (Number.isFinite(numericValue)) {
    return `${numericValue.toFixed(2).replace('.', ',')} €`;
  }

  const readableValue = rawValue.replace('.', ',');

  if (readableValue.includes('€')) {
    return readableValue;
  }

  return `${readableValue} €`;
}

function normalizeSearchValue(value) {
  return String(value || '').trim().toLowerCase();
}

function isListCategory(categoryTitle) {
  const normalizedTitle = normalizeSearchValue(categoryTitle);

  return LIST_CATEGORIES.some(
    (category) => normalizeSearchValue(category) === normalizedTitle
  );
}

function itemMatchesSearch(item, category, searchTerm) {
  const normalizedSearchTerm = normalizeSearchValue(searchTerm);

  if (!normalizedSearchTerm) {
    return true;
  }

  const searchableText = [
    item.name,
    item.description,
    item.price,
    category.title,
    item.imageAlt
  ]
    .map(normalizeSearchValue)
    .join(' ');

  return searchableText.includes(normalizedSearchTerm);
}

function MenuPage() {
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('alle');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    const loadMenu = async () => {
      try {
        const data = await getMenu();
        setCategories(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Fehler beim Laden der Speisekarte:', error);
        setCategories([]);
      }
    };

    loadMenu();
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setSelectedItem(null);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  const visibleCategories = useMemo(() => {
    const categoriesByActiveFilter =
      activeCategory === 'alle'
        ? categories
        : categories.filter((cat) => cat.id === activeCategory);

    return categoriesByActiveFilter
      .map((category) => ({
        ...category,
        items: (category.items || []).filter((item) =>
          itemMatchesSearch(item, category, searchTerm)
        )
      }))
      .filter((category) => category.items.length > 0);
  }, [categories, activeCategory, searchTerm]);

  const getFallbackImage = (category) => {
    return category?.placeholderImage?.trim() || DEFAULT_PLACEHOLDER;
  };

  const getDisplayImage = (item, category) => {
    if (item?.image?.trim()) return item.image;
    return getFallbackImage(category);
  };

  const isPlaceholderImage = (item) => !item?.image?.trim();

  const handleImageError = (event, fallbackSrc) => {
    if (event.currentTarget.dataset.fallbackApplied === 'true') return;

    event.currentTarget.src = fallbackSrc;
    event.currentTarget.dataset.fallbackApplied = 'true';
  };

  const openItemModal = (item, category) => {
    setSelectedItem({
      ...item,
      image: getDisplayImage(item, category),
      isPlaceholder: isPlaceholderImage(item),
      description:
        item.description ||
        'Weitere Details zu dieser Speise erhalten Sie gerne direkt bei uns im Lokal.'
    });
  };

  const closeModal = () => {
    setSelectedItem(null);
  };

  const hasSearchTerm = searchTerm.trim().length > 0;

  return (
    <Section>
      <SectionTitle>Speisekarte</SectionTitle>
      <HeaderImage />

      <Intro>
        Bodenständige Schmankerl, klassische Heurigenküche und passende Getränke
        für einen gemütlichen Abend.
      </Intro>

      <Toolbar>
        <SearchWrapper>
          <SearchIcon>🔎</SearchIcon>
          <SearchInput
            type="search"
            placeholder="Speisen & Getränke suchen ..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            aria-label="Speisekarte durchsuchen"
          />

          {hasSearchTerm && (
            <ClearSearchButton
              type="button"
              onClick={() => setSearchTerm('')}
              aria-label="Suche löschen"
            >
              ×
            </ClearSearchButton>
          )}
        </SearchWrapper>

        <FilterBar>
          <FilterButton
            type="button"
            $active={activeCategory === 'alle'}
            onClick={() => setActiveCategory('alle')}
          >
            Alles
          </FilterButton>

          {categories.map((category) => (
            <FilterButton
              type="button"
              key={category.id}
              $active={activeCategory === category.id}
              onClick={() => setActiveCategory(category.id)}
            >
              {category.title}
            </FilterButton>
          ))}
        </FilterBar>
      </Toolbar>

      {!visibleCategories.length && (
        <EmptyState>
          {hasSearchTerm
            ? 'Keine passenden Speisen oder Getränke gefunden.'
            : 'Derzeit sind keine Menüeinträge verfügbar.'}
        </EmptyState>
      )}

      {visibleCategories.map((category) => {
        const renderAsList = isListCategory(category.title);

        return (
          <CategorySection key={category.id}>
            <CategoryTitle>{category.title}</CategoryTitle>

            {renderAsList ? (
              <ListPanel>
                {category.items?.map((item, index) => (
                  <ListRow key={`${category.id}-${item.name}-${index}`}>
                    <div>
                      <ListItemName>{item.name}</ListItemName>

                      {item.description && (
                        <ListItemDescription>{item.description}</ListItemDescription>
                      )}
                    </div>

                    <ListItemPrice>{formatPrice(item.price)}</ListItemPrice>
                  </ListRow>
                ))}
              </ListPanel>
            ) : (
              <Grid>
                {category.items?.map((item, index) => {
                  const imageSrc = getDisplayImage(item, category);
                  const fallbackSrc = getFallbackImage(category);
                  const showPlaceholderLabel = isPlaceholderImage(item);

                  return (
                    <Card
                      key={`${category.id}-${item.name}-${index}`}
                      onClick={() => openItemModal(item, category)}
                    >
                      <ImageWrapper>
                        <ItemImage
                          src={imageSrc}
                          alt={item.imageAlt || item.name}
                          loading="lazy"
                          onError={(event) => handleImageError(event, fallbackSrc)}
                        />

                        <ImageOverlay>
                          <OverlayText>
                            <OverlayTitle>{item.name}</OverlayTitle>
                            <OverlaySubtitle>Details ansehen</OverlaySubtitle>
                          </OverlayText>
                        </ImageOverlay>

                        {showPlaceholderLabel && (
                          <PlaceholderLabel>Bild folgt</PlaceholderLabel>
                        )}
                      </ImageWrapper>

                      <CardContent>
                        <ItemRow>
                          <ItemName>{item.name}</ItemName>
                          <ItemPrice>{formatPrice(item.price)}</ItemPrice>
                        </ItemRow>
                      </CardContent>
                    </Card>
                  );
                })}
              </Grid>
            )}
          </CategorySection>
        );
      })}

      <AllergenNote>
        Allergenhinweise können beim Personal erfragt werden.
      </AllergenNote>

      {selectedItem && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={(event) => event.stopPropagation()}>
            <CloseButton onClick={closeModal} aria-label="Schließen">
              ×
            </CloseButton>

            <ModalImage
              src={selectedItem.image}
              alt={selectedItem.imageAlt || selectedItem.name}
            />

            <ModalBody>
              <ModalTitleRow>
                <ModalTitle>{selectedItem.name}</ModalTitle>
                <ModalPrice>{formatPrice(selectedItem.price)}</ModalPrice>
              </ModalTitleRow>

              <ModalDescription>{selectedItem.description}</ModalDescription>

              <ModalInfoBox>
                <ModalInfoTitle>Hinweis</ModalInfoTitle>
                Allergenhinweise können beim Personal erfragt werden.
              </ModalInfoBox>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </Section>
  );
}

export default MenuPage;